import React, { useCallback, useEffect, useRef } from 'react';
import PropType from 'prop-types';

import { useTheme, makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import Slider from '@material-ui/core/Slider';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import MultiSelectMenu from '../shared/multiSelect';

import {
    PRICE_AMOUNT_MIN,
    PRICE_AMOUNT_MAX,
    PRICE_AMOUNT_MARKS,
} from '../../constants';
import {
    CourseCategory,
    CourseLanguage,
    CourseMedium,
} from '../../models/enums';

import Faq from '../shared/faq';

const useStyles = makeStyles(theme => ({
    root: {
        flexGrow: 1,
        flex: 1,
        [theme.breakpoints.only('xs')]: {
            width: '280px',
            paddingLeft: '10px',
            paddingRight: '10px',
        },
        [theme.breakpoints.only('sm')]: {
            width: '280px',
            paddingLeft: '15px',
            paddingRight: '15px',
        },
        maxWidth: '280px',
        paddingTop: '50px',
        paddingLeft: '15px',
        paddingRight: '0px',
        paddingBottom: '50px',
    },
    fieldHeading: {
        fontSize: '16px',
        fontWeight: 600,
        marginBottom: '6px',
    },
    input: {
        ...theme.form.textField,
        borderRadius: '10px',
    },
    select: {
        borderRadius: '10px',
        '&:focus': {
            borderRadius: '10px',
            backgroundColor: '#FFFFFF!important',
        },
    },
    sliderContianer: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
    },
    slider: {
        // This is a hotfix for a known bug in material ui:
        // https://github.com/mui-org/material-ui/issues/13455
        width: '95%'
    }
}));

export default function CourseGroupSearchForm(props) {
    const classes = useStyles(useTheme());
    const { input, setInput, onSubmit } = props;
    const resetTriggered = useRef(false);

    const onChangeField = useCallback(
        e => {
            const name = e.target.name, value = e.target.value;
            setInput(input => {
                return {...input, [name]: value === ''?null:value};
            });
        },
        [setInput]
    );
    const onSubmitForm = useCallback(
        e => {
            e.preventDefault();
            onSubmit();
        },
        [onSubmit]
    );

    const onChangePriceRange = useCallback(
        (e, newValue) => {
            const [priceAmountMin, priceAmountMax] = newValue;
            setInput(input => {
                return {
                    ...input,
                    priceAmountMin: priceAmountMin === PRICE_AMOUNT_MIN?null:priceAmountMin,
                    priceAmountMax: priceAmountMax === PRICE_AMOUNT_MAX?null:priceAmountMax,
                };
            });
        },
        [setInput]
    );

    const onChangeDiscountFilter = useCallback(
        (e) => {
            const checked = e.target.checked;
            setInput(input => {
                return {
                    ...input,
                    first_course_discount_applied: checked ? true : null,
                };
            });
        },
        [setInput]
    );

    const onReset = useCallback(
        (e) => {
            e.preventDefault();
            const clearedState = {
                query: null,
                medium: null,
                language: null,
                category: null,
                courseLevel: null,
                priceAmountMin: null,
                priceAmountMax: null,
                totalDurationMinuteMin: null,
                totalDurationMinuteMax: null,
                ratingAverageMin: null,
                ratingAverageMax: null,
                first_course_discount_applied: null,
            };
            setInput(clearedState);
            resetTriggered.current = true;
        },
        [setInput]
    );

    // Trigger search after reset state update
    useEffect(() => {
        if (resetTriggered.current) {
            resetTriggered.current = false;
            onSubmit();
        }
    }, [input, onSubmit]);

    return (
        <Container className={classes.root}>
            <form onSubmit={onSubmitForm}>
                <Grid container direction='column' spacing={2}>
                    <Grid item xs={12}>
                        <Typography variant="h3" component="h3" className={classes.fieldHeading}>關鍵字</Typography>
                        <TextField
                            id='query'
                            name='query'
                            margin='dense'
                            value={input.query || ''}
                            onChange={onChangeField}
                            fullWidth
                            variant='outlined'
                            InputProps={
                                { className: classes.input }
                            }
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="h3" component="h3" className={classes.fieldHeading}>範疇</Typography>
                        <MultiSelectMenu
                            id='category'
                            name='category'
                            wrapperClassName={classes.input}
                            className={classes.select}
                            rootClassName={classes.select}
                            enums={CourseCategory}
                            variant='outlined'
                            margin='dense'
                            value={input.category}
                            onChange={onChangeField}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="h3" component="h3" className={classes.fieldHeading}>形式</Typography>
                        <MultiSelectMenu
                            id='medium'
                            name='medium'
                            wrapperClassName={classes.input}
                            className={classes.select}
                            enums={CourseMedium}
                            variant='outlined'
                            margin='dense'
                            value={input.medium}
                            onChange={onChangeField}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="h3" component="h3" className={classes.fieldHeading}>語言</Typography>
                        <MultiSelectMenu
                            id='language'
                            name='language'
                            wrapperClassName={classes.input}
                            className={classes.select}
                            enums={CourseLanguage}
                            variant='outlined'
                            margin='dense'
                            value={input.language}
                            onChange={onChangeField}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="h3" component="h3" className={classes.fieldHeading}>價錢</Typography>
                        <Container className={classes.sliderContianer}>
                        <Slider
                            className={classes.slider}
                            value={[
                                input.priceAmountMin || PRICE_AMOUNT_MIN,
                                input.priceAmountMax || PRICE_AMOUNT_MAX,
                            ]}
                            onChange={onChangePriceRange}
                            valueLabelDisplay="auto"
                            aria-labelledby="price-slider"
                            marks={PRICE_AMOUNT_MARKS}
                            min={PRICE_AMOUNT_MIN}
                            max={PRICE_AMOUNT_MAX}
                        />
                        </Container>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={input.first_course_discount_applied === true}
                                    onChange={onChangeDiscountFilter}
                                    name="first_course_discount_applied"
                                    color="primary"
                                />
                            }
                            label="只顯示優惠課程"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <Button
                                    type='button'
                                    variant='outlined'
                                    color='primary'
                                    size='large'
                                    fullWidth
                                    onClick={onReset}
                                >
                                    重設
                                </Button>
                            </Grid>
                            <Grid item xs={6}>
                                <Button
                                    type='submit'
                                    variant='contained'
                                    color='primary'
                                    size='large'
                                    fullWidth
                                >
                                    搜尋
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12}>
                        <Faq screen='search_screen' />
                    </Grid>
                </Grid>
            </form>
        </Container>
    );
}
CourseGroupSearchForm.propTypes = {
    input: PropType.object.isRequired,
    setInput: PropType.func.isRequired,
    onSubmit: PropType.func.isRequired,
}